import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useLang } from "@/lib/lang";
import { EASE, Eyebrow, MaskLines } from "@/components/motion";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const BASE = process.env.REACT_APP_BACKEND_URL;

const RoundBtn = ({ onClick, label, children, testid }) => (
  <button type="button" onClick={onClick} aria-label={label} data-testid={testid} className="grid h-12 w-12 place-items-center rounded-full border border-white/15 text-white/80 transition-[background-color,color,transform] duration-300 hover:bg-white hover:text-[#05070f] active:scale-95">
    {children}
  </button>
);

export const AppScreenshots = ({ app }) => {
  const { t, lang } = useLang();
  const [shots, setShots] = useState([]);
  const [open, setOpen] = useState(null);
  const track = useRef(null);

  useEffect(() => {
    let alive = true;
    axios.get(`${BASE}/api/apps/${app.slug}/screenshots`)
      .then(({ data }) => { if (alive) setShots(Array.isArray(data) ? data : []); })
      .catch(() => { if (alive) setShots([]); });
    return () => { alive = false; };
  }, [app.slug]);

  if (!shots.length) return null;
  const scroll = (dir) => track.current?.scrollBy({ left: dir * track.current.clientWidth * 0.7, behavior: "smooth" });
  const step = (dir) => setOpen((i) => (i + dir + shots.length) % shots.length);

  return (
    <section data-testid="app-screenshots" className="overflow-hidden py-16 sm:py-24">
      <div className="container-x flex flex-wrap items-end justify-between gap-6">
        <div>
          <Eyebrow>{t.detail.screensEyebrow}</Eyebrow>
          <h2 key={lang} className="section-title mt-6 font-display font-bold"><MaskLines inView lines={[t.detail.screens]} /></h2>
        </div>
        {shots.length > 1 && (
          <div className="flex gap-2">
            <RoundBtn onClick={() => scroll(-1)} label="Previous" testid="screenshots-prev-button"><ArrowLeft className="h-4 w-4" /></RoundBtn>
            <RoundBtn onClick={() => scroll(1)} label="Next" testid="screenshots-next-button"><ArrowRight className="h-4 w-4" /></RoundBtn>
          </div>
        )}
      </div>
      <div ref={track} className="shots-track mt-12 flex snap-x snap-mandatory gap-5 overflow-x-auto pb-6">
        {shots.map((s, i) => (
          <motion.button
            key={s.id}
            type="button"
            data-testid={`screenshot-${i}`}
            onClick={() => setOpen(i)}
            initial={{ opacity: 0, y: 50, rotate: 2 }}
            whileInView={{ opacity: 1, y: 0, rotate: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 1, ease: EASE, delay: Math.min(i, 5) * 0.08 }}
            className="group w-[62vw] shrink-0 snap-start sm:w-[250px] lg:w-[270px]"
          >
            <div className="rounded-[2.4rem] border border-white/15 bg-[#0b101d] p-2 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.9)] transition-transform duration-700 ease-[cubic-bezier(.16,1,.3,1)] group-hover:-translate-y-2 group-hover:-rotate-1">
              <div className="aspect-[9/19.5] overflow-hidden rounded-[2rem] bg-black">
                <img src={`${BASE}${s.url}`} alt={`${app.name} screenshot ${i + 1}`} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(.16,1,.3,1)] group-hover:scale-105" />
              </div>
            </div>
          </motion.button>
        ))}
      </div>
      <Dialog open={open !== null} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent data-testid="screenshot-lightbox" className="max-w-[min(92vw,480px)] border-white/10 bg-[#05070f] p-4">
          <DialogTitle className="sr-only">{app.name} screenshot</DialogTitle>
          {open !== null && (
            <img src={`${BASE}${shots[open].url}`} alt={`${app.name} screenshot ${open + 1}`} className="mx-auto max-h-[78vh] w-auto rounded-[1.6rem] object-contain" />
          )}
          {shots.length > 1 && (
            <div className="flex items-center justify-between">
              <RoundBtn onClick={() => step(-1)} label="Previous" testid="lightbox-prev-button"><ArrowLeft className="h-4 w-4" /></RoundBtn>
              <span className="font-mono text-xs text-white/50">{(open ?? 0) + 1} / {shots.length}</span>
              <RoundBtn onClick={() => step(1)} label="Next" testid="lightbox-next-button"><ArrowRight className="h-4 w-4" /></RoundBtn>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
